import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MinusIcon, PlusIcon, XIcon } from 'lucide-react'
import {
	Accordion, AccordionContent, AccordionItem, AccordionTrigger,
	Button,
	Dialog, DialogClose, DialogContent, DialogTitle,
	IconButton,
} from '@rentflowdev/ui'
import { DocumentBlockPreview } from '@/components/document-block-preview'
import { DEFAULT_LOCALE } from '@/types'
import type { DocumentTemplate } from '@/types'

export interface TemplateVersion {
	id: string
	label: string
	isCurrent?: boolean
}

export type TemplateHistoryKind = 'created' | 'modified'

export interface TemplateHistoryEntry {
	id: string
	kind: TemplateHistoryKind
	at: string
}

interface TemplatePreviewDialogProps {
	template: DocumentTemplate | null
	subtitle?: string
	versions?: TemplateVersion[]
	history?: TemplateHistoryEntry[]
	totalPages?: number
	open: boolean
	onOpenChange: (open: boolean) => void
	onEdit?: () => void
	onDownloadPdf?: () => void
}

function formatHistoryDate(iso: string): string {
	const date = new Date(iso)
	const today = new Date()
	const yesterday = new Date(today)
	yesterday.setDate(yesterday.getDate() - 1)
	const time = date.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	if (date.toDateString() === today.toDateString()) return `Сегодня, ${time}`
	if (date.toDateString() === yesterday.toDateString()) return `Вчера, ${time}`
	return `${date.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${time}`
}

export function TemplatePreviewDialog({
	template,
	subtitle,
	versions = [],
	history = [],
	totalPages = 1,
	open,
	onOpenChange,
	onEdit,
	onDownloadPdf,
}: TemplatePreviewDialogProps) {
	const { t } = useTranslation()
	const [page, setPage] = useState(1)
	const [zoom, setZoom] = useState(120)

	if (!template) return null

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				style={{ width: 1080, maxWidth: '95vw', height: '90vh' }}
				className="flex flex-col overflow-hidden rounded-(--radius-lg) p-0 gap-0 shadow-(--shadow-lg)"
			>
				<div className="flex flex-col gap-1 border-b border-(--color-border) p-4">
					<div className="flex items-center gap-3">
						<DialogTitle className="flex-1 text-2xl leading-8 font-[500] tracking-[-0.6px] text-(--color-foreground)">
							{t('documents.preview.title', { name: template.title })}
						</DialogTitle>
						<Button
							variant="secondary"
							size="sm"
							iconRight={<ChevronDownIcon className="size-4" />}
							onClick={onDownloadPdf}
						>
							{t('documents.preview.downloadPdf')}
						</Button>
						<DialogClose
							render={
								<button
									type="button"
									aria-label={t('documents.preview.close')}
									className="rounded-(--radius-sm) p-0.5 text-(--color-muted-foreground) transition-colors hover:text-(--color-foreground)"
								/>
							}
						>
							<XIcon className="size-4" />
						</DialogClose>
					</div>
					{subtitle && (
						<p className="text-base leading-6 text-(--color-muted-foreground)">{subtitle}</p>
					)}
				</div>

				<div className="flex flex-1 overflow-hidden">
					<div className="flex flex-1 flex-col overflow-hidden">
						<div className="flex flex-1 items-stretch justify-center overflow-auto bg-(--color-muted)/20 p-6">
							<div
								className="w-full max-w-[733px] rounded-(--radius-md) border border-(--color-border) bg-(--color-background) shadow-sm"
								style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
							>
								{template.blocks && template.blocks.length > 0 ? (
									<DocumentBlockPreview blocks={template.blocks} />
								) : (
									<p className="p-10 text-center text-sm text-(--color-muted-foreground)">
										{t('documents.preview.placeholder')}
									</p>
								)}
							</div>
						</div>
						<div className="flex items-center justify-between border-t border-(--color-border) p-4">
							<div className="flex items-center gap-1">
								<IconButton
									variant="ghost"
									size="sm"
									aria-label={t('documents.preview.prevPage')}
									disabled={page <= 1}
									onClick={() => setPage((p) => Math.max(1, p - 1))}
								>
									<ChevronLeftIcon className="size-4" />
								</IconButton>
								<div className="flex h-10 items-center rounded-(--radius-md) border border-(--color-border) px-3">
									<span className="text-sm font-[500] leading-6 text-(--color-primary)">
										{t('documents.preview.page', { page })}
									</span>
									<span className="text-sm font-[500] leading-6 text-(--color-muted-foreground)">&nbsp;/ {totalPages}</span>
								</div>
								<IconButton
									variant="ghost"
									size="sm"
									aria-label={t('documents.preview.nextPage')}
									disabled={page >= totalPages}
									onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								>
									<ChevronRightIcon className="size-4" />
								</IconButton>
							</div>

							<div className="flex items-center gap-1">
								<IconButton
									variant="ghost"
									size="sm"
									aria-label={t('documents.preview.zoomOut')}
									disabled={zoom <= 50}
									onClick={() => setZoom((z) => Math.max(50, z - 10))}
								>
									<MinusIcon className="size-4" />
								</IconButton>
								<span className="inline-flex h-10 items-center justify-center px-3 text-sm font-[500] leading-6 text-(--color-muted-foreground)">
									{zoom}%
								</span>
								<IconButton
									variant="ghost"
									size="sm"
									aria-label={t('documents.preview.zoomIn')}
									disabled={zoom >= 200}
									onClick={() => setZoom((z) => Math.min(200, z + 10))}
								>
									<PlusIcon className="size-4" />
								</IconButton>
							</div>
						</div>
					</div>

					<div className="relative flex w-[334px] shrink-0 flex-col border-l border-(--color-border)">
						<div className="flex flex-col gap-7 overflow-auto p-4 pb-[76px]">
							<section className="flex flex-col gap-3">
								<h3 className="text-base font-[500] leading-6 text-(--color-foreground)">
									{t('documents.preview.versions')}
								</h3>
								{versions.length === 0 ? (
									<p className="text-sm text-(--color-muted-foreground)">
										{t('documents.preview.noVersions')}
									</p>
								) : (
									<div className="flex flex-col gap-2">
										{versions.map((v) => (
											<button
												key={v.id}
												type="button"
												className="flex h-6 items-center gap-2 text-left text-sm font-[500] leading-6 text-(--color-brand) transition-colors hover:text-(--color-brand-hover)"
											>
												<span>{v.label}</span>
												{v.isCurrent && (
													<span className="text-(--color-brand)">{t('documents.preview.current')}</span>
												)}
											</button>
										))}
									</div>
								)}
							</section>

							<Accordion type="single" defaultValue="history" collapsible>
								<AccordionItem value="history" className="border-none">
									<AccordionTrigger className="py-0 text-base font-[500] leading-6 text-(--color-foreground)">
										{t('documents.preview.history')}
									</AccordionTrigger>
									<AccordionContent className="pt-4 pb-0">
										{history.length === 0 ? (
											<p className="text-sm text-(--color-muted-foreground)">
												{t('documents.preview.noHistory')}
											</p>
										) : (
											<ul className="flex flex-col gap-1">
												{history.map((entry) => (
													<li key={entry.id} className="flex items-center justify-between py-1">
														<span className="text-sm leading-5 text-(--color-foreground)">
															{t(`documents.preview.${entry.kind}`)}
														</span>
														<span className="text-sm leading-5 text-(--color-muted-foreground)">
															{formatHistoryDate(entry.at)}
														</span>
													</li>
												))}
											</ul>
										)}
									</AccordionContent>
								</AccordionItem>
							</Accordion>
						</div>

						<div className="absolute bottom-0 left-0 right-0 border-t border-(--color-border) bg-(--color-background) p-4">
							<Button variant="primary" size="full" onClick={onEdit}>
								{t('documents.preview.editTemplate')}
							</Button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
